import { createContext, useContext, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  // Tatsaechlich aktive Erscheinung (system aufgeloest).
  resolved: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = 'saldify-theme';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

// Darkmode gilt nur im eingeloggten App-Bereich, nicht auf Marketing-Seiten.
function inAppArea(): boolean {
  const p = window.location.pathname;
  return p.startsWith('/app') || p.startsWith('/onboarding');
}

function applyClass(resolved: 'light' | 'dark') {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark' && inAppArea());
}

/**
 * Theme-Verwaltung (hell/dunkel/system). Persistiert die Wahl in localStorage
 * und setzt die `.dark`-Klasse am <html>. Bei 'system' folgt es der
 * Betriebssystem-Einstellung und reagiert live auf Aenderungen.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system';
  });

  const resolved: 'light' | 'dark' = mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;
  const location = useLocation();

  // Bei Theme- ODER Routenwechsel neu anwenden (App-Bereich vs. Marketing).
  useEffect(() => {
    applyClass(resolved);
  }, [resolved, location.pathname]);

  // Bei 'system' auf OS-Wechsel reagieren.
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyClass(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  function setMode(m: ThemeMode) {
    setModeState(m);
    localStorage.setItem(STORAGE_KEY, m);
  }

  return <ThemeContext.Provider value={{ mode, setMode, resolved }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme muss innerhalb von <ThemeProvider> verwendet werden.');
  return ctx;
}
